import { Formik, type FormikHelpers } from 'formik';
import { useCallback } from 'react';
import * as Yup from 'yup';
import { useToastUpdater } from '../../../../../context/toastContext/toastContext';
import { useAuthUpdater } from '../../../../../lib/auth/context/authContext';
import useAuthSession from '../../../../../lib/auth/hooks/useAuthSession';
import { dateOrderPreference } from '../../../../../pages/profile';
import userService from '../../../../../services/user.service';
import type { HubConfig } from '../../../../../types/API/user/User';
import Button from '../../../../common/Buttons/Button/Button';
import CardActions from '../../../../common/Card/CardActions';
import FormFieldError from '../../../../common/Form/Errors/FormFieldError';
import ListBox from '../../../../common/Form/ListBox/ListBox';
import Switch from '../../../../common/Form/Switch/Switch';
import Typography from '../../../../common/Typography/Typography';
import type { SlideProps } from '../../types';

const updateProfileFormSchema = Yup.object().shape({
    table_filters: Yup.boolean(),
    date_asc_desc: Yup.object()
        .shape({
            id: Yup.number(),
            value: Yup.string().oneOf(['asc', 'dsc']),
            label: Yup.string(),
        })
        .required('Table ordering is required'),
});

const UserPreferences = ({ nextSlide }: SlideProps) => {
    const { session } = useAuthSession({ required: true });
    const { refreshSession } = useAuthUpdater();
    const { user } = session!;
    const { addToast } = useToastUpdater();

    const initialValues = {
        table_filters: user.config.hub?.table_filters || false,
        date_asc_desc:
            dateOrderPreference.find(({ value }) => value === user.config.hub?.date_asc_desc) || dateOrderPreference[0],
    };

    type Values = typeof initialValues;

    const handleSubmit = useCallback(
        async (values: Values, { setSubmitting }: FormikHelpers<Values>) => {
            setSubmitting(true);

            try {
                await userService.updateUserConfig({
                    ...user.config.hub,
                    table_filters: values.table_filters,
                    date_asc_desc: values.date_asc_desc.value as Required<HubConfig>['date_asc_desc'],
                });
            } catch (error) {
                addToast('Your profile preferences could not be updated. Please try again', 'error', '');
                setSubmitting(false);
                return;
            }

            await refreshSession();

            addToast('Your profile preferences have been updated', 'success', '');

            if (nextSlide) {
                nextSlide();
            }
            // We've purposely disabled this as we don't need addToast or refreshSession to be in the
            // dependency changes.
        },
        [user?.id, user.firstname, user.lastname], // eslint-disable-line react-hooks/exhaustive-deps
    );
    return (
        <Formik validationSchema={updateProfileFormSchema} initialValues={initialValues} onSubmit={handleSubmit}>
            {({ errors, values, touched, handleSubmit, isSubmitting, handleChange }) => (
                <form className="space-y-2 flex flex-col items-center overflow-visible py-1" onSubmit={handleSubmit}>
                    <fieldset className="space-y-1">
                        <Typography as="p" variant="meta">
                            Set up user preferences, choose how your selections persist whilst navigating Hub. You can
                            revisit this later in your account.
                        </Typography>
                        <div className="flex flex-col gap-2 type-meta md:flex-row">
                            {/* keep search across page yes/no */}
                            <div className="space-y-0.25 flex gap-0.5 items-center justify-between w-full">
                                <Typography as="p" variant="meta" color="pine">
                                    Keep table filters across page
                                </Typography>
                                <Switch
                                    label="Keep search across page"
                                    onChange={(_isOn, event) => handleChange(event)}
                                    name="table_filters"
                                    isOn={values.table_filters}
                                />
                                {touched.table_filters && errors.table_filters ? (
                                    <FormFieldError>{errors.table_filters as string}</FormFieldError>
                                ) : null}
                            </div>
                        </div>

                        {/* table ordering desc/asc */}
                        <div className="space-y-0.25  w-full">
                            <ListBox
                                label="Table ordering"
                                name="date_asc_desc"
                                onSelect={(_selectedItem, event) => {
                                    handleChange(event);
                                }}
                                selectedItem={values.date_asc_desc}
                                error={touched.date_asc_desc && !!errors.date_asc_desc}
                                items={dateOrderPreference}
                            />
                            {touched.date_asc_desc && errors.date_asc_desc ? (
                                <FormFieldError>{errors.date_asc_desc as string}</FormFieldError>
                            ) : null}
                        </div>
                    </fieldset>
                    <CardActions className="w-full max-w-[16rem]">
                        <Button type="submit" loading={isSubmitting} disabled={isSubmitting} className="w-full">
                            Update Details
                        </Button>
                    </CardActions>
                </form>
            )}
        </Formik>
    );
};

export default UserPreferences;
